/** Read-state for the background-task notification bell.
 *
 *  A single "sweep" timestamp (epoch ms) is persisted in localStorage: every
 *  terminal task that finished at or before it counts as read. `markAllRead()`
 *  bumps the sweep to now, which clears the fresh-terminal contribution to the
 *  badge. The bell and the Background Tasks page share this one key so that
 *  visiting the page also marks everything finished-so-far as read.
 */
import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'console:bgTaskBell:readSweepTs';

function readStored(): number {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const n = raw ? Number(raw) : 0;
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
}

export interface BgTaskReadState {
  /** Epoch ms; terminal tasks finished at/before this are considered read. */
  readSweepTs: number;
  /** Mark every currently-finished task read (bumps the sweep to now). */
  markAllRead: () => void;
}

export function useBgTaskReadState(): BgTaskReadState {
  const [readSweepTs, setReadSweepTs] = useState<number>(readStored);

  // Keep multiple mounted consumers (bell + page) in sync within the tab and
  // across tabs (storage event fires in *other* tabs only).
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setReadSweepTs(readStored());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const markAllRead = useCallback(() => {
    const now = Date.now();
    try {
      localStorage.setItem(STORAGE_KEY, String(now));
    } catch {
      // Non-persistent (private mode / quota) — still update in-memory state so
      // the badge clears for this session.
    }
    setReadSweepTs(now);
  }, []);

  return { readSweepTs, markAllRead };
}
